import {
  Box,
  ScrollArea,
  Switch,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "@salestrak/ui";
import {
  PageHeader,
  PageHeaderActions,
  PageHeaderSubtitle,
  PageHeaderTitle,
  SETTINGS_CONTENT_MAX_WIDTH,
  SettingsBackBar,
  SettingsBackBarClearance,
  SettingsPageGutter,
} from "#app/components/page-header";
import { CopyIconButton } from "#app/components/copy-icon-button";
import { WebhookActionsMenu } from "./webhook-actions-menu";
import { SettingsGroup, SettingsGroupRow } from "./settings-group";
import {
  HTTP_METHODS,
  joinLabels,
  optionLabel,
  pinnedStageId,
  targetLabel,
  triggerLabel,
  webhookSource,
  webhookTargetNames,
} from "./webhook-options";
import { type WebhookItem } from "#app/hooks/settings/use-webhook-mutations";
import { NONE_OPTION_LABEL } from "#app/lib/custom-field-utils";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { formatDate } from "#app/lib/utils/format-date";

interface WebhookDetailViewProps {
  webhook: WebhookItem;
  onBack: () => void;
  onEdit: () => void;
  onToggleArchived: () => void;
  onDelete: () => void;
}

// A read-only value trailing a settings row, on the API key detail's rung.
function Value({ children }: { children: string }) {
  return (
    <Text size="sm" variant="muted">
      {children}
    </Text>
  );
}

export function WebhookDetailView({
  webhook,
  onBack,
  onEdit,
  onToggleArchived,
  onDelete,
}: WebhookDetailViewProps) {
  const { stageMap, workflowMap, isHydrated } = useWorkspaceData();
  const stageId = pinnedStageId(webhook.when);
  // A pinned stage the workspace does not hold was deleted after the save;
  // the row says so instead of going missing.
  const targetText = stageId
    ? (targetLabel(webhookTargetNames(stageId, stageMap, workflowMap)) ??
      (isHydrated ? "This stage no longer exists" : "Loading…"))
    : null;
  const dateLabel = webhook.creationDate ? formatDate(webhook.creationDate) : null;
  const enabled = !webhook.archived;
  const joins = joinLabels(webhook.resource, webhook.joins);

  return (
    <Box direction="column" fill="flex" position="relative">
      <SettingsBackBar label="API" onClick={onBack} />
      <ScrollArea>
        <SettingsBackBarClearance />
        <PageHeader centered pinnedBack>
          <PageHeaderTitle>{webhook.name}</PageHeaderTitle>
          <PageHeaderActions>
            <WebhookActionsMenu
              archived={webhook.archived}
              onEdit={onEdit}
              onToggleArchived={onToggleArchived}
              onDelete={onDelete}
            />
          </PageHeaderActions>
          {dateLabel ? (
            <PageHeaderSubtitle>Created {dateLabel}</PageHeaderSubtitle>
          ) : null}
        </PageHeader>
        <Box align="center" direction="column" paddingX="md" paddingY="md">
          <Box direction="column" fill="width" gap="2xl" maxWidth={SETTINGS_CONTENT_MAX_WIDTH}>
            <SettingsGroup label="Endpoint">
              {/* The state is a control, not a caption: the switch IS the
                * Enable/Disable action the "…" menu also offers, one press
                * instead of two. The tooltip names the press by state. */}
              <SettingsGroupRow label="Enabled">
                <Tooltip>
                  <TooltipTrigger render={<Box direction="row" align="center" />}>
                    <Switch
                      aria-label="Enable webhook"
                      checked={enabled}
                      onCheckedChange={() => onToggleArchived()}
                    />
                  </TooltipTrigger>
                  <TooltipPopup>{enabled ? "Disable" : "Enable"}</TooltipPopup>
                </Tooltip>
              </SettingsGroupRow>
              {/* The URL rides the label column, which is the row's only
                * shrinking slot: it ellipsizes (tooltip for the rest) instead
                * of pushing the copy control off the row. */}
              <SettingsGroupRow
                label="URL"
                subtitle={
                  <Text size="sm" variant="muted" truncate overflowTooltip selectable>
                    {webhook.url}
                  </Text>
                }
              >
                <CopyIconButton text={webhook.url} what="URL" />
              </SettingsGroupRow>
              <SettingsGroupRow label="Method">
                <Value>
                  {optionLabel(HTTP_METHODS, webhook.method, (webhook.method ?? "post").toUpperCase())}
                </Value>
              </SettingsGroupRow>
              <SettingsGroupRow
                label="Webhook ID"
                subtitle={
                  <Text size="sm" variant="muted" mono selectable>
                    {webhook.webhookId}
                  </Text>
                }
              >
                <CopyIconButton text={webhook.webhookId} what="Webhook ID" />
              </SettingsGroupRow>
            </SettingsGroup>

            {/* No sentence here: the rows below ARE the readout. The form
              * keeps its live sentence, where it confirms a choice in
              * progress rather than restating settled facts. */}
            <SettingsGroup label="Event">
              <SettingsGroupRow label="Source">
                <Value>{webhookSource(webhook.resource).label}</Value>
              </SettingsGroupRow>
              <SettingsGroupRow label="Trigger">
                <Value>{triggerLabel(webhook.resource, webhook.trigger)}</Value>
              </SettingsGroupRow>
              {targetText ? (
                <SettingsGroupRow label="Target">
                  <Value>{targetText}</Value>
                </SettingsGroupRow>
              ) : null}
              <SettingsGroupRow label="Includes">
                <Value>{joins.length > 0 ? joins.join(", ") : NONE_OPTION_LABEL}</Value>
              </SettingsGroupRow>
            </SettingsGroup>

            <SettingsPageGutter />
          </Box>
        </Box>
      </ScrollArea>
    </Box>
  );
}
